// Shuffle execution helper; loads a sample, opens the winner overlay, and runs animation.
import {
	COVER_PLACEHOLDER_SRC,
	SHUFFLE_SAMPLE_LIMIT,
} from '../../../shared/config/constants'
import { t } from '../../../shared/config/translations'
import { normalizeReleaseRating } from '../../../shared/filters/collectionFilters'
import type { ShuffleSampleItem } from '../../../shared/types/shuffle'
import { getShuffleSample, recordShuffleStart } from '../../api/shuffle'
import { getCurrentSettingsCache } from '../../cache/settings'
import type { FiltersState } from '../../components/filters/filterState'
import { openOverlay } from '../../components/Overlay'
import { renderReleaseLazy } from '../../components/release/Release'
import {
	attachShuffleAnimation,
	preloadShuffleSample,
	runReleaseStaggerReveal,
} from '../../components/ShuffleAnimationLayer'
import { haptic } from '../../utils/haptics'

export type PreparedShuffle = {
	preloadedCovers: Awaited<ReturnType<typeof preloadShuffleSample>>
	sample: ShuffleSampleItem[]
}

/** Loads and preloads one shuffle result so it can start without click-time waiting. */
export async function prepareShuffle(
	activeFilters: FiltersState,
	signal?: AbortSignal,
): Promise<PreparedShuffle | null> {
	const { sample } = await getShuffleSample(activeFilters, {
		limit: SHUFFLE_SAMPLE_LIMIT,
		signal,
	})
	if (!sample || sample.length === 0) return null

	const preloadedCovers = await preloadShuffleSample(sample, signal)
	if (!preloadedCovers.length) return null

	return {
		preloadedCovers,
		sample,
	}
}

/** Runs one shuffle attempt and returns whether a result flow started. */
export function runShuffle(
	prepared: PreparedShuffle,
	onComplete: () => void,
): boolean {
	const { preloadedCovers, sample } = prepared
	const skipShuffleAnimation = getCurrentSettingsCache()?.skipShuffleAnimation

	const winner = sample[0]
	if (!winner) return false

	const hiddenUntil = winner.history?.hiddenUntil
		? new Date(winner.history.hiddenUntil)
		: undefined
	const releaseInstanceId = winner.instance_id ?? undefined
	openOverlay(
		renderReleaseLazy(
			winner.id,
			releaseInstanceId,
			{
				date_added: winner.date_added,
				isFavorite: !!winner.isFavorite,
				rating: normalizeReleaseRating(winner.rating),
				preview: {
					artists:
						winner.artists
							?.map((artist) => artist.name)
							.filter(Boolean)
							.join(', ') ?? '',
					coverSrc: winner.cover_image || COVER_PLACEHOLDER_SRC,
					isHidden: !!(
						winner.history?.isHidden || winner.history?.hiddenUntil
					),
					title: winner.title ?? '',
					year: String(winner.master_year || winner.year || ''),
				},
				history: winner.history
					? {
							dimIfHidden: false,
							hiddenUntil,
							historyDates: winner.history.historyDates?.map(
								(d) => new Date(d),
							),
							isHidden: winner.history.isHidden,
							listenCount: winner.history.listenCount,
							unknownDateCount: winner.history.unknownDateCount,
						}
					: null,
			},
			{
				link: {
					url: winner.uri,
					text: t('nav.showOnDiscogs'),
				},
			},
		),
	)

	const host = document.querySelector<HTMLElement>('.overlay-inner-content')
	// The overlay header sits outside the animated release host.
	host?.classList.add('shuffle-in-progress')
	const complete = () => {
		host?.classList.remove('shuffle-in-progress')
		onComplete()
	}
	requestAnimationFrame(() => {
		if (!host?.isConnected) {
			complete()
			return
		}
		if (skipShuffleAnimation) {
			void runReleaseStaggerReveal(host).finally(complete)
			return
		}

		// Without the animation the reveal follows at once; a start tap would blur into it.
		void haptic('tap')
		const attached = attachShuffleAnimation(sample, {
			onComplete: complete,
			preloadedCovers,
		})
		if (!attached) complete()
	})

	// Keep usage recording independent of animation and playback availability.
	void recordShuffleStart().catch((error) => {
		console.warn('Failed to record shuffle start', error)
	})

	return true
}
